import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createDrawerSocket } from "./connection";
import { createStrokeStore, ERASER_SCALE } from "./strokes";

const FLUSH_MS = 40;

const drawerIdentity = () => {
  try {
    let id = sessionStorage.getItem("draw_drawer_id");
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("draw_drawer_id", id);
    }
    return id;
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
};

/**
 * Drawer (phone) side of a draw session. Without a session code — an old app
 * showing the baked QR — it runs local-only: the canvas works, nothing is sent.
 * phase: "connecting" | "ready" | "taken" | "gone" | "local"
 */
const useDrawer = (code) => {
  const store = useMemo(() => createStrokeStore(), []);
  const [phase, setPhase] = useState(code ? "connecting" : "local");
  const [started, setStarted] = useState(false);
  const socketRef = useRef(null);
  const current = useRef(null); // { id, pending: [] } while a finger is down
  const flushTimer = useRef(null);

  useEffect(() => {
    if (!code) return;
    const socket = createDrawerSocket(code, drawerIdentity());
    socketRef.current = socket;

    // Runs on every (re)connect: the server's list is the truth, so anything
    // drawn while offline is dropped here rather than left out of sync.
    socket.on("connect", () => {
      socket.emit("drawer:hello", null, (res) => {
        if (!res?.ok) return setPhase(res?.reason === "taken" ? "taken" : "gone");
        current.current = null;
        store.reset(res.strokes);
        if (res.status === "drawing") setStarted(true);
        setPhase("ready");
      });
    });
    socket.on("connect_error", (err) => {
      if (err?.message === "session_not_found") setPhase("gone");
    });
    socket.on("draw:ended", () => setPhase("gone"));

    return () => {
      clearInterval(flushTimer.current);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [code, store]);

  const send = useCallback((event, payload) => {
    const socket = socketRef.current;
    if (socket?.connected) socket.emit(event, payload);
  }, []);

  const flush = useCallback(() => {
    const stroke = current.current;
    if (!stroke || !stroke.pending.length) return;
    send("stroke:pts", { id: stroke.id, pts: stroke.pending });
    stroke.pending = [];
  }, [send]);

  const start = useCallback(() => {
    setStarted(true);
    send("drawer:start");
  }, [send]);

  const strokeStart = useCallback(
    (pt, { tool, color, size }) => {
      const stroke = {
        id: Math.random().toString(36).slice(2, 12),
        tool,
        color,
        size: tool === "eraser" ? size * ERASER_SCALE : size,
        pt,
      };
      current.current = { id: stroke.id, pending: [] };
      store.start(stroke);
      send("stroke:start", stroke);
      flushTimer.current = setInterval(flush, FLUSH_MS);
    },
    [store, send, flush]
  );

  const strokeMove = useCallback(
    (pts) => {
      const stroke = current.current;
      if (!stroke) return;
      store.pts({ id: stroke.id, pts });
      stroke.pending.push(...pts);
    },
    [store]
  );

  const strokeEnd = useCallback(() => {
    clearInterval(flushTimer.current);
    flush();
    current.current = null;
  }, [flush]);

  const undo = useCallback(() => {
    store.undo();
    send("stroke:undo");
  }, [store, send]);

  const clear = useCallback(() => {
    store.clear();
    send("stroke:clear");
  }, [store, send]);

  return {
    phase,
    started,
    store,
    start,
    strokeStart,
    strokeMove,
    strokeEnd,
    undo,
    clear,
  };
};

export default useDrawer;
